/**
 * Pure, testable loading policy for the public commercial configuration
 * (prices + limits) used by the credits/pricing pages.
 *
 * Guarantees:
 *  - never hangs: every attempt has a controlled timeout;
 *  - retries once (only for timeouts/errors, never after a valid answer);
 *  - an empty or partial answer is NOT treated as success;
 *  - the last known-good value may only be shown as a visual fallback, and a
 *    fallback state never authorises starting a checkout.
 */
import type { PricingMap, UsageLimits } from "./commercial-config";

export type CommercialConfigValue = {
  pricing: PricingMap;
  limits: UsageLimits;
  /** Confirmado pelo servidor; quando false nenhuma compra pode arrancar. */
  paymentsAvailable?: boolean;
  paymentsBlockedReason?: string | null;
};

export type ConfigLoadStatus = "loading" | "ready" | "fallback" | "error";

export type ConfigLoadState = {
  status: ConfigLoadStatus;
  config: CommercialConfigValue | null;
  error?: string;
};

export const CONFIG_TIMEOUT_MS = 8_000;

export function isUsablePricing(
  value: CommercialConfigValue | null | undefined,
  requiredKeys: string[] = [],
): boolean {
  if (!value || typeof value !== "object") return false;
  const pricing = value.pricing;
  if (!pricing || typeof pricing !== "object") return false;
  if (!Object.keys(pricing).length) return false;
  return requiredKeys.every((key) => {
    const entry = pricing[key];
    return (
      Boolean(entry) && Number.isFinite(entry.amount) && entry.amount > 0 && Boolean(entry.label)
    );
  });
}

export async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error("timeout")), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function loadCommercialConfig(options: {
  fetcher: () => Promise<CommercialConfigValue>;
  requiredKeys?: string[];
  timeoutMs?: number;
  retries?: number;
  fallback?: CommercialConfigValue | null;
}): Promise<ConfigLoadState> {
  const timeoutMs = options.timeoutMs ?? CONFIG_TIMEOUT_MS;
  const attempts = Math.max(1, (options.retries ?? 1) + 1);
  const requiredKeys = options.requiredKeys ?? [];
  let lastError = "Não foi possível carregar os preços.";

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const value = await withTimeout(options.fetcher(), timeoutMs);
      if (isUsablePricing(value, requiredKeys)) {
        return { status: "ready", config: value };
      }
      lastError = "A configuração de preços devolvida está incompleta.";
    } catch (error) {
      lastError =
        error instanceof Error && error.message === "timeout"
          ? "O serviço de preços demorou demasiado a responder."
          : "Não foi possível contactar o serviço de preços.";
    }
  }

  if (isUsablePricing(options.fallback, requiredKeys)) {
    return { status: "fallback", config: options.fallback ?? null, error: lastError };
  }
  return { status: "error", config: null, error: lastError };
}

/** A checkout may only start from a fully confirmed configuration. */
export function canStartCheckout(state: ConfigLoadState, priceId: string): boolean {
  if (state.status !== "ready" || !state.config) return false;
  // Fail-closed: ambiente de pagamentos incoerente bloqueia o checkout.
  if (state.config.paymentsAvailable === false) return false;
  const entry = state.config.pricing[priceId];
  return Boolean(entry) && Number.isFinite(entry.amount) && entry.amount > 0;
}
